import React, { ReactNode } from "react"

import {
  ErrorBoundary as ReactErrorBoundary,
  useErrorBoundary as useReactErrorBoundary,
} from "react-error-boundary"
import { useTranslation } from "react-i18next"

import { useAirbrake } from "@components/AirbrakeProvider"
import { logger } from "@helpers/logger"

import newrelic from "../../newrelic"

interface FallbackProps {
  error: Error
  resetErrorBoundary: (...args: Array<unknown>) => void
}

const Fallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  const { t } = useTranslation()

  return (
    <div role="alert">
      <p>{t("errorBoundary_fallbackTitle")}:</p>
      <pre style={{ color: "red" }}>{error.message}</pre>
      <button onClick={resetErrorBoundary} type="button">
        {t("errorBoundary_fallbackText")}
      </button>
    </div>
  )
}

interface ErrorBoundaryProps {
  children: ReactNode
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
}: ErrorBoundaryProps) => {
  const { notifier: airbrake } = useAirbrake()

  const logError = (
    error: Error,
    info: { componentStack: string | null | undefined },
  ) => {
    newrelic?.noticeError(error, {
      params: info.componentStack || "Component stack unavailable",
    })
    // Notify Airbrake about the error
    airbrake?.notify({
      error,
      params: info.componentStack || "Component stack unavailable",
    })
  }

  return (
    <ReactErrorBoundary
      FallbackComponent={Fallback}
      onReset={(details) => {
        // Reset the state of your app so the error doesn't happen again
        logger.error(details)
      }}
      onError={(error, info) =>
        logError(error, info as { componentStack: string | null | undefined })
      }
    >
      {children}
    </ReactErrorBoundary>
  )
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useErrorBoundary = () => useReactErrorBoundary()

export default ErrorBoundary
